import { createTheme } from '@mui/material/styles'

import { colors, radius, controlHeight, fontFamily } from './tokens'
import ChevronDownIcon from '../components/icons/chevron-down-icon'

// Тема действует на все компоненты MUI в приложении: TextField, Dialog, Menu,
// Chip, Checkbox, Switch и так далее. Свои обёртки (Button, Select, ...) могут
// переопределять отдельные значения - тема лишь задаёт основу.
const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      dark: colors.primaryHover,
      light: colors.primaryLight,
      contrastText: colors.white,
    },
    error: { main: colors.error },
    warning: { main: colors.warning },
    success: { main: colors.success },
    text: {
      primary: colors.text,
      secondary: colors.textSecondary,
      disabled: colors.textDisabled,
    },
    divider: colors.divider,
    background: { default: colors.white, paper: colors.white },
  },

  shape: { borderRadius: radius.input },

  typography: {
    fontFamily,
    fontSize: 14,
    h1: { fontSize: 28, lineHeight: '36px', fontWeight: 600 },
    h2: { fontSize: 24, lineHeight: '32px', fontWeight: 600 },
    h3: { fontSize: 20, lineHeight: '28px', fontWeight: 600 },
    h4: { fontSize: 18, lineHeight: '24px', fontWeight: 600 },
    h5: { fontSize: 16, lineHeight: '24px', fontWeight: 600 },
    h6: { fontSize: 14, lineHeight: '20px', fontWeight: 600 },
    subtitle1: { fontSize: 14, lineHeight: '20px', fontWeight: 500 },
    subtitle2: { fontSize: 12, lineHeight: '16px', fontWeight: 500 },
    body1: { fontSize: 14, lineHeight: '20px', fontWeight: 400 },
    body2: { fontSize: 12, lineHeight: '16px', fontWeight: 400 },
    caption: { fontSize: 12, lineHeight: '16px', fontWeight: 400 },
    button: { fontSize: 14, lineHeight: '20px', fontWeight: 500, textTransform: 'none' },
  },

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: controlHeight.default,
          padding: '8px 16px',
          borderRadius: radius.button,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: '20px',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        sizeSmall: { minHeight: controlHeight.small, padding: '4px 12px', fontSize: 13 },
        outlined: {
          borderColor: colors.border,
          color: colors.textButton,
          '&:hover': { borderColor: colors.borderHover, backgroundColor: colors.surfaceHover },
        },
        containedPrimary: {
          '&:hover': { backgroundColor: colors.primaryHover },
        },
        text: { padding: '8px 12px' },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: 14, lineHeight: '20px' },
        input: {
          '&::placeholder': { color: colors.textMuted, opacity: 1 },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.input,
          backgroundColor: colors.white,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
          '&:hover:not(.Mui-disabled):not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.focus,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.focus,
            borderWidth: 1.5,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: colors.errorBorder },
          '&.Mui-disabled': {
            backgroundColor: colors.surface,
            color: colors.textDisabled,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: colors.divider },
        },
        input: {
          padding: '8px 12px',
          height: 20,
          boxSizing: 'content-box',
        },
        multiline: { padding: 0 },
        inputMultiline: { height: 'auto' },
        inputSizeSmall: { padding: '4px 10px' },
        notchedOutline: { borderColor: colors.border },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
          lineHeight: '20px',
          color: colors.textSecondary,
          '&.Mui-focused': { color: colors.primary },
          '&.Mui-error': { color: colors.error },
        },
        // Поле стало ниже (36 вместо 56) - подпись садится по центру
        outlined: {
          transform: 'translate(12px, 8px) scale(1)',
          '&.MuiInputLabel-shrink': { transform: 'translate(14px, -8px) scale(0.85)' },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: 12, lineHeight: '16px', margin: '4px 2px 0', color: colors.textMuted },
      },
    },

    MuiSelect: {
      defaultProps: { IconComponent: ChevronDownIcon },
      styleOverrides: {
        icon: {
          color: colors.textSecondary,
          fontSize: 16,
          top: 'calc(50% - 8px)',
          right: 10,
        },
      },
    },

    MuiNativeSelect: {
      defaultProps: { IconComponent: ChevronDownIcon },
    },

    MuiAutocomplete: {
      defaultProps: { popupIcon: <ChevronDownIcon /> },
      styleOverrides: {
        // У Autocomplete свои отступы поля (9px и 7.5px): на наши 8px 12px они
        // складывались, и поле выходило выше остальных. Приводим к высоте 36;
        // селекторы усилены, потому что у Autocomplete они сами длинные
        root: {
          '&&&& .MuiOutlinedInput-root': { paddingTop: 0, paddingBottom: 0, paddingLeft: 6, minHeight: 36 },
          '&&&& .MuiOutlinedInput-root .MuiAutocomplete-input': {
            padding: '8px 6px', height: 20, boxSizing: 'content-box',
          },
        },
        popupIndicator: { color: colors.textSecondary, '& svg': { fontSize: 16 } },

        paper: { borderRadius: radius.menu },
        option: { fontSize: 14, minHeight: 36 },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radius.menu,
          border: `1px solid ${colors.divider}`,
          boxShadow: '0 8px 24px rgba(17, 19, 21, 0.12)',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          lineHeight: '20px',
          minHeight: 36,
          padding: '8px 12px',
          '&:hover': { backgroundColor: colors.surface },
          '&.Mui-selected': {
            backgroundColor: colors.primaryLight,
            '&:hover': { backgroundColor: colors.primaryLight },
          },
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: { borderRadius: radius.menu },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.modal,
          boxShadow: '0 24px 48px rgba(17, 19, 21, 0.2)',
          // На телефоне оставляем по 16px с краёв вместо стандартных 32
          '@media (max-width:600px)': {
            margin: 16,
            width: 'calc(100% - 32px)',
            maxWidth: 'calc(100% - 32px)',
          },
          // Диалог на весь экран (fullScreen) - без полей: иначе по краям видна
          // страница под ним, и это уже не экран, а окно
          '&.MuiDialog-paperFullScreen': {
            '@media (max-width:600px)': {
              margin: 0,
              width: '100%',
              maxWidth: '100%',
              borderRadius: 0,
            },
          },
        },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: colors.backdrop } },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          fontSize: 18,
          lineHeight: '22px',
          fontWeight: 600,
          color: colors.text,
          borderBottom: `1px solid ${colors.divider}`,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          fontSize: 14,
          color: colors.textSecondary,
          // MUI сам убирает верхний отступ у содержимого после заголовка;
          // у нас под заголовком линия, поэтому отступ нужен
          '.MuiDialogTitle-root + &': { paddingTop: 20 },
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '16px 24px', gap: 8, '& > :not(:first-of-type)': { marginLeft: 0 } },
      },
    },

    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: radius.card },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius.card,
          border: `1px solid ${colors.border}`,
          boxShadow: 'none',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          height: 23,
          borderRadius: radius.pill,
          fontSize: 12,
          fontWeight: 500,
          backgroundColor: colors.divider,
          color: colors.textSecondary,
        },
        label: { padding: '0 10px' },
        sizeSmall: { height: 20 },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.text,
          borderRadius: radius.tooltip,
          fontSize: 12,
          lineHeight: '16px',
          padding: '6px 10px',
        },
        arrow: { color: colors.text },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2, backgroundColor: colors.primary },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: 14,
          fontWeight: 500,
          color: colors.textButton,
          '&.Mui-selected': { color: colors.primary, fontWeight: 600 },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          fontSize: 14,
          lineHeight: '20px',
          color: colors.text,
          borderBottom: `1px solid ${colors.divider}`,
        },
        head: {
          padding: '10px 16px',
          fontSize: 12,
          lineHeight: '15px',
          fontWeight: 600,
          backgroundColor: colors.surface,
          color: colors.text,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: { '&.MuiTableRow-hover:hover': { backgroundColor: colors.divider } },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: { color: colors.border, '&.Mui-checked': { color: colors.primary } },
      },
    },

    MuiLink: {
      defaultProps: { underline: 'hover' },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.input, fontSize: 14 },
      },
    },
  },
})

export default theme
